import { type DownloadPriority } from "@/shared/reader";
import { type MessagePayload } from "@/shared/webworker";

export enum IndexState {
  Empty = "empty",
  Building = "building",
  Built = "built",
}

export enum RawMessageEvent {
  Error = "error",
  Initialized = "initialized",
  /** Emitted when the time index is built, building, or cleared. Transfers timestamps + sequences. */
  TimeIndexStateChange = "time-index-state-change",
  /** Emitted when download priority changes (foreground/background/false) */
  LoadingStateChange = "loading-state-change",
}

export interface RawMessageEventPayloadMap {
  [RawMessageEvent.Error]: undefined;
  [RawMessageEvent.Initialized]: undefined;
  [RawMessageEvent.TimeIndexStateChange]: {
    state: IndexState;
    /** ArrayBuffer containing BigUint64Array of timestamps per display position */
    timestamps?: ArrayBuffer;
    /** ArrayBuffer containing Uint32Array of sequences per display position */
    sequences?: ArrayBuffer;
  };
  [RawMessageEvent.LoadingStateChange]: { state: DownloadPriority | false };
}

export function isErrorEvent(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    RawMessageEvent,
    RawMessageEventPayloadMap[RawMessageEvent.Error]
  >
> {
  return event.data.type === RawMessageEvent.Error;
}

export function isInitializedEvent(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    RawMessageEvent,
    RawMessageEventPayloadMap[RawMessageEvent.Initialized]
  >
> {
  return event.data.type === RawMessageEvent.Initialized;
}

export function isTimeIndexStateChangeEvent(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    RawMessageEvent,
    RawMessageEventPayloadMap[RawMessageEvent.TimeIndexStateChange]
  >
> {
  return event.data.type === RawMessageEvent.TimeIndexStateChange;
}

export function isLoadingStateChangeEvent(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    RawMessageEvent,
    RawMessageEventPayloadMap[RawMessageEvent.LoadingStateChange]
  >
> {
  return event.data.type === RawMessageEvent.LoadingStateChange;
}
